import { supabase } from '../config/supabase';
import {
  Structure,
  StructureWithStats,
  StructureWithLearners,
  StructureFilters,
  StructureStatus
} from '../types';

// Helper pour convertir les données DB en Structure
const toStructure = (row: Record<string, unknown>): Structure => ({
  id: row.id as string,
  organizationId: row.organization_id as string,
  name: row.name as string,
  externalClientId: row.external_client_id as string | undefined,
  contactName: row.contact_name as string | undefined,
  contactEmail: row.contact_email as string | undefined,
  contactPhone: row.contact_phone as string | undefined,
  address: row.address as string | undefined,
  notes: row.notes as string | undefined,
  status: row.status as StructureStatus,
  createdAt: new Date(row.created_at as string),
  updatedAt: new Date(row.updated_at as string)
});

export const structuresService = {
  // Récupérer toutes les structures (avec filtres optionnels)
  async getAll(filters?: StructureFilters): Promise<Structure[]> {
    let query = supabase
      .from('structures')
      .select('*')
      .order('name');

    if (filters?.organizationId) {
      query = query.eq('organization_id', filters.organizationId);
    }

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    if (filters?.externalClientId) {
      query = query.eq('external_client_id', filters.externalClientId);
    }

    if (filters?.search) {
      query = query.or(`name.ilike.%${filters.search}%,external_client_id.ilike.%${filters.search}%`);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching structures:', error);
      throw new Error('Impossible de charger les structures');
    }

    return (data || []).map(toStructure);
  },

  // Récupérer les structures d'une organisation
  async getByOrganization(organizationId: string): Promise<Structure[]> {
    return this.getAll({ organizationId });
  },

  // Récupérer les structures avec statistiques
  async getWithStats(organizationId: string): Promise<StructureWithStats[]> {
    const structures = await this.getByOrganization(organizationId);

    // Récupérer les stats pour chaque structure
    const structuresWithStats: StructureWithStats[] = await Promise.all(
      structures.map(async (structure) => {
        const { data: learners } = await supabase
          .from('learners')
          .select('status')
          .eq('structure_id', structure.id);

        const learnersList = learners || [];
        const learnersCount = learnersList.length;
        const completedCount = learnersList.filter(l => l.status === 'completed').length;
        const activeCount = learnersList.filter(l => l.status === 'active').length;

        return {
          ...structure,
          learnersCount,
          completedCount,
          activeCount,
          completionRate: learnersCount > 0 ? Math.round((completedCount / learnersCount) * 100) : 0
        };
      })
    );

    return structuresWithStats;
  },

  // Récupérer une structure par ID
  async getById(id: string): Promise<Structure | null> {
    const { data, error } = await supabase
      .from('structures')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      console.error('Error fetching structure:', error);
      throw new Error('Impossible de charger la structure');
    }

    return data ? toStructure(data) : null;
  },

  // Récupérer une structure par numéro client externe
  async getByExternalClientId(externalClientId: string, organizationId?: string): Promise<Structure | null> {
    let query = supabase
      .from('structures')
      .select('*')
      .eq('external_client_id', externalClientId);

    if (organizationId) {
      query = query.eq('organization_id', organizationId);
    }

    const { data, error } = await query.maybeSingle();

    if (error) {
      console.error('Error fetching structure by client ID:', error);
      throw new Error('Impossible de trouver la structure');
    }

    return data ? toStructure(data) : null;
  },

  // Récupérer une structure avec ses apprenants
  async getWithLearners(id: string): Promise<StructureWithLearners | null> {
    const structure = await this.getById(id);
    if (!structure) return null;

    const { data: learners } = await supabase
      .from('learners')
      .select('*')
      .eq('structure_id', id)
      .order('fullname');

    const { data: orgData } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', structure.organizationId)
      .single();

    return {
      ...structure,
      learners: (learners || []).map(l => ({
        id: l.id,
        organizationId: l.organization_id,
        structureId: l.structure_id,
        lmsUserId: l.lms_user_id,
        email: l.email,
        fullname: l.fullname,
        jobTitle: l.job_title,
        department: l.department,
        status: l.status,
        enrolledAt: l.enrolled_at ? new Date(l.enrolled_at) : undefined,
        completedAt: l.completed_at ? new Date(l.completed_at) : undefined,
        createdAt: new Date(l.created_at),
        updatedAt: new Date(l.updated_at)
      })),
      organization: orgData ? {
        id: orgData.id,
        name: orgData.name,
        slug: orgData.slug,
        contactName: orgData.contact_name,
        contactEmail: orgData.contact_email,
        contactPhone: orgData.contact_phone,
        logoUrl: orgData.logo_url,
        address: orgData.address,
        notes: orgData.notes,
        status: orgData.status,
        contractStart: orgData.contract_start ? new Date(orgData.contract_start) : undefined,
        contractEnd: orgData.contract_end ? new Date(orgData.contract_end) : undefined,
        createdAt: new Date(orgData.created_at),
        updatedAt: new Date(orgData.updated_at)
      } : undefined
    };
  },

  // Créer une structure
  async create(structure: Omit<Structure, 'id' | 'createdAt' | 'updatedAt'>): Promise<Structure> {
    const { data, error } = await supabase
      .from('structures')
      .insert({
        organization_id: structure.organizationId,
        name: structure.name,
        external_client_id: structure.externalClientId,
        contact_name: structure.contactName,
        contact_email: structure.contactEmail,
        contact_phone: structure.contactPhone,
        address: structure.address,
        notes: structure.notes,
        status: structure.status || 'active'
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating structure:', error);
      if (error.code === '23505') {
        throw new Error('Ce numéro client existe déjà pour cette organisation');
      }
      throw new Error('Impossible de créer la structure');
    }

    return toStructure(data);
  },

  // Mettre à jour une structure
  async update(id: string, updates: Partial<Omit<Structure, 'id' | 'createdAt' | 'updatedAt'>>): Promise<Structure> {
    const updateData: Record<string, unknown> = {};

    if (updates.organizationId !== undefined) updateData.organization_id = updates.organizationId;
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.externalClientId !== undefined) updateData.external_client_id = updates.externalClientId;
    if (updates.contactName !== undefined) updateData.contact_name = updates.contactName;
    if (updates.contactEmail !== undefined) updateData.contact_email = updates.contactEmail;
    if (updates.contactPhone !== undefined) updateData.contact_phone = updates.contactPhone;
    if (updates.address !== undefined) updateData.address = updates.address;
    if (updates.notes !== undefined) updateData.notes = updates.notes;
    if (updates.status !== undefined) updateData.status = updates.status;

    const { data, error } = await supabase
      .from('structures')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating structure:', error);
      if (error.code === '23505') {
        throw new Error('Ce numéro client existe déjà pour cette organisation');
      }
      throw new Error('Impossible de mettre à jour la structure');
    }

    return toStructure(data);
  },

  // Supprimer une structure
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('structures')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting structure:', error);
      throw new Error('Impossible de supprimer la structure');
    }
  },

  // Statistiques pour une structure
  async getStats(structureId: string): Promise<{
    learnersCount: number;
    activeCount: number;
    completedCount: number;
    inactiveCount: number;
    completionRate: number;
  }> {
    const { data: learners, error } = await supabase
      .from('learners')
      .select('status')
      .eq('structure_id', structureId);

    if (error) {
      console.error('Error fetching structure stats:', error);
      throw new Error('Impossible de charger les statistiques');
    }

    const list = learners || [];
    const learnersCount = list.length;
    const activeCount = list.filter(l => l.status === 'active').length;
    const completedCount = list.filter(l => l.status === 'completed').length;
    const inactiveCount = list.filter(l => l.status === 'inactive').length;

    return {
      learnersCount,
      activeCount,
      completedCount,
      inactiveCount,
      completionRate: learnersCount > 0 ? Math.round((completedCount / learnersCount) * 100) : 0
    };
  },

  // Rechercher des structures par numéro client (pour la landing page)
  async searchByClientNumber(clientNumber: string): Promise<StructureWithStats | null> {
    const structure = await this.getByExternalClientId(clientNumber);
    if (!structure) return null;

    const stats = await this.getStats(structure.id);

    return {
      ...structure,
      ...stats
    };
  }
};
